/**
 * Employee Detail Panel — slide-out panel with inline editing for
 * employee entries.
 *
 * Laid out to match the Fair Entitlements Guarantee claim template so a
 * practitioner filling in the FEG Branch DB import can work top to
 * bottom: personal details, employment, end of employment, then one
 * section per entitlement category.
 *
 * Each entitlement category shows the stored amount (which stays
 * canonical for every downstream consumer) alongside what the captured
 * FEG inputs calculate to, with a "Use calculated" action when the two
 * disagree. The stored figure is never silently overwritten — a Proof
 * of Debt amount the employee has signed off on must be able to beat
 * the arithmetic.
 */

import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import { api } from "@cvx/_generated/api";
import { titleCaseCompanyName } from "@cvx/lib/companyName";
import {
  computeFegDerived,
  isFegEmploymentBasis,
  netFegRecoverable,
  splitEmployeeName,
  sumEmployeeEntitlements,
  sumFegRecoverable,
  sumPaymentsAfterTermination,
  DIRECTOR_RELATIONSHIP_LABELS,
  EMPLOYMENT_BASIS_LABELS,
  EMPLOYMENT_STATES,
  FEG_EMPLOYMENT_BASIS,
  EMPLOYMENT_TYPE_LABELS,
  RESIDENCY_LABELS,
  TERMINATION_TYPE_LABELS,
  type DerivedAmount,
  type FegCaseDates,
} from "@cvx/lib/employeeEntitlements";
import {
  X,
  FileText,
  ChevronRight,
  ChevronDown,
  Pencil,
  Check,
  Trash2,
  Info,
  Sparkles,
  AlertTriangle,
  EyeOff,
  Eye,
} from "lucide-react";
import { cn } from "@/utils/misc";
import { Button } from "@/ui/primitives/button";
import { Input } from "@/ui/primitives/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/primitives/select";
import { Badge } from "@/ui/primitives/badge";
import type { Id } from "@cvx/_generated/dataModel";
import {
  ChangeHistory,
  type ResolvedHistoryUser,
} from "../shared-panels/ChangeHistory";
import { NotesSection } from "../shared-panels/NotesSection";
import {
  ConfirmEditDialog,
  type PendingEdit,
} from "../shared-panels/ConfirmEditDialog";
import {
  EMPLOYEE_FIELD_FORMATTERS,
  EMPLOYEE_FIELD_LABELS,
} from "./historyFormatters";
import { RelatedPartyBadge } from "../../../_shared/RelatedPartyBadge";
import { relatedPartyExplanation } from "../../../_shared/relatedPartyShared";
import type { EmployeeEntryUI, EntitlementSourceUI } from "./types";
import { EntitlementEstimatesSection } from "./EntitlementEstimatesSection";
import { DocumentDetail } from "../../documents/DocumentDetail";
import { PodStatusSection } from "@/components/jobs/phases/recoveries/podStatusDisplay";

/** How the recorded figures were arrived at, shown under the headline total. */
const ENTITLEMENT_SOURCE_HINT: Record<EntitlementSourceUI, string> = {
  document: "From the company's records",
  proof_of_debt:
    "From the employee's proof of debt \u00B7 not independently verified",
  pod_admitted: "As admitted on adjudication",
  manual: "Entered by the practitioner",
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** The nested FEG input objects, keyed as the mutation expects them. */
type DetailSection =
  | "wagesDetail"
  | "annualLeaveDetail"
  | "leaveLoadingDetail"
  | "longServiceLeaveDetail"
  | "pilnDetail"
  | "redundancyDetail"
  | "paymentsAfterTermination";

const EM_DASH = "\u2014";

function fmtCurrency(n?: number): string {
  if (n == null) return EM_DASH;
  return n.toLocaleString("en-AU", {
    style: "currency",
    currency: "AUD",
    minimumFractionDigits: 2,
  });
}

function fmtSignedCurrency(n: number): string {
  const sign = n > 0 ? "+" : "";
  return `${sign}${fmtCurrency(n)}`;
}

function fmtDateLong(d?: string): string {
  if (!d) return EM_DASH;
  try {
    return new Date(d + "T00:00:00").toLocaleDateString("en-AU", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return d;
  }
}

function fmtYears(n?: number): string {
  if (n == null) return EM_DASH;
  return `${n.toFixed(2)} years`;
}

function fmtHours(n?: number): string {
  if (n == null) return EM_DASH;
  return `${n} hrs`;
}

function fmtWeeks(n?: number): string {
  if (n == null) return EM_DASH;
  return n === 1 ? "1 week" : `${n} weeks`;
}

// ---------------------------------------------------------------------------
// Editable field
// ---------------------------------------------------------------------------

function EditableField({
  label,
  value,
  displayValue,
  field,
  type = "text",
  onSave,
  isViewer,
}: {
  label: string;
  value?: string | number | null;
  displayValue?: string;
  field: string;
  type?: "text" | "date" | "number";
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onSave: (field: string, value: any) => void;
  isViewer?: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(String(value ?? ""));

  const handleSave = () => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const parsed: any =
      type === "number"
        ? draft
          ? parseFloat(draft)
          : undefined
        : draft || undefined;
    onSave(field, parsed);
    setEditing(false);
  };

  if (editing) {
    return (
      <div className="flex items-center justify-between gap-2 py-2">
        <span className="shrink-0 text-sm text-muted-foreground">{label}</span>
        <div className="flex items-center gap-1">
          <Input
            type={type}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            className="h-7 w-32 text-sm"
            onKeyDown={(e) => e.key === "Enter" && handleSave()}
            autoFocus
          />
          <button
            onClick={handleSave}
            className="rounded p-1 text-emerald-600 hover:bg-emerald-50"
          >
            <Check className="h-3.5 w-3.5" />
          </button>
          <button
            onClick={() => setEditing(false)}
            className="rounded p-1 text-muted-foreground hover:bg-muted"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="group flex items-center justify-between gap-4 py-2">
      <span className="shrink-0 text-sm text-muted-foreground">{label}</span>
      <div className="flex items-center gap-1.5">
        <span className="text-right text-sm text-foreground">
          {displayValue || String(value ?? EM_DASH)}
        </span>
        {!isViewer && (
          <button
            onClick={() => {
              setDraft(String(value ?? ""));
              setEditing(true);
            }}
            className="rounded p-0.5 text-muted-foreground/0 transition-colors hover:bg-muted group-hover:text-muted-foreground"
          >
            <Pencil className="h-3 w-3" />
          </button>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Enum select field
// ---------------------------------------------------------------------------

/**
 * Sentinel for the "Not set" option. Radix `Select` cannot take an empty
 * string as an item value, so a placeholder is required — it must not
 * collide with any real enum key (`none` is a legitimate value of the
 * relationship-to-director taxonomy).
 */
const UNSET = "__unset__";

function EnumField({
  label,
  value,
  options,
  onChange,
  isViewer,
}: {
  label: string;
  value?: string;
  /** `[value, label]` pairs in display order. */
  options: Array<[string, string]>;
  onChange: (value: string | undefined) => void;
  isViewer?: boolean;
}) {
  const displayed = options.find(([v]) => v === value)?.[1];

  return (
    <div className="flex items-center justify-between gap-4 py-2">
      <span className="shrink-0 text-sm text-muted-foreground">{label}</span>
      {isViewer ? (
        <span className="text-right text-sm text-foreground">
          {displayed ?? EM_DASH}
        </span>
      ) : (
        <Select
          value={value ?? UNSET}
          onValueChange={(v) => onChange(v === UNSET ? undefined : v)}
        >
          {/*
            Sized to its content rather than a fixed width: the FEG
            labels are verbatim and range from "Casual" to "No
            notification of termination", so any single width either
            truncates the long ones or leaves the short ones adrift.
            The cap keeps the longest value from crowding the label,
            and the trigger's own truncation is the safety valve.
          */}
          <SelectTrigger className="h-7 w-auto min-w-[6rem] max-w-[65%] text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={UNSET}>Not set</SelectItem>
            {options.map(([v, l]) => (
              <SelectItem key={v} value={v}>
                {l}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Read-only derived row
// ---------------------------------------------------------------------------

function DerivedRow({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  hint?: string;
}) {
  return (
    <div className="flex items-start justify-between gap-4 py-2">
      <div className="min-w-0">
        <span className="text-sm text-muted-foreground">{label}</span>
        {hint && (
          <div className="text-[11px] leading-snug text-muted-foreground/70">
            {hint}
          </div>
        )}
      </div>
      {/* Matches the EditableField value styling: derived rows sit
          directly alongside editable ones in the employment and wage
          sections, so a different face and weight reads as a glitch. */}
      <span className="shrink-0 text-right text-sm text-foreground">
        {value}
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Entitlement amount row (stored, with the calculated figure beside it)
// ---------------------------------------------------------------------------

function AmountRow({
  label,
  entKey,
  allEntitlements,
  derived,
  onSave,
  isViewer,
}: {
  label: string;
  entKey: string;
  allEntitlements: Record<string, number | undefined>;
  /** Omitted for categories FEG has no formula for (e.g. super). */
  derived?: DerivedAmount;
  onSave: (entitlements: Record<string, number | undefined>) => void;
  isViewer?: boolean;
}) {
  const value = allEntitlements[entKey];
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(String(value ?? ""));

  const commit = (next: number | undefined) => {
    onSave({ ...allEntitlements, [entKey]: next });
  };

  const handleSave = () => {
    commit(draft ? parseFloat(draft) : undefined);
    setEditing(false);
  };

  const calculated = derived?.calculated;
  // Worth surfacing when the arithmetic disagrees with what's stored, or
  // when nothing is stored yet and the inputs already give us an answer.
  const showCalculated =
    calculated !== undefined && (derived?.hasVariance || value === undefined);

  return (
    <div className="group py-2">
      <div className="flex items-center justify-between gap-4">
        <span className="text-sm text-muted-foreground">{label}</span>
        {editing ? (
          <div className="flex items-center gap-1">
            <Input
              type="number"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              className="h-7 w-28 text-right font-mono text-sm"
              onKeyDown={(e) => e.key === "Enter" && handleSave()}
              autoFocus
            />
            <button
              onClick={handleSave}
              className="rounded p-1 text-emerald-600 hover:bg-emerald-50"
            >
              <Check className="h-3.5 w-3.5" />
            </button>
            <button
              onClick={() => setEditing(false)}
              className="rounded p-1 text-muted-foreground hover:bg-muted"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-1.5">
            <span className="font-mono text-sm font-semibold tabular-nums text-foreground">
              {fmtCurrency(value)}
            </span>
            {!isViewer && (
              <button
                onClick={() => {
                  setDraft(String(value ?? ""));
                  setEditing(true);
                }}
                className="rounded p-0.5 text-muted-foreground/0 transition-colors hover:bg-muted group-hover:text-muted-foreground"
              >
                <Pencil className="h-3 w-3" />
              </button>
            )}
          </div>
        )}
      </div>

      {showCalculated && !editing && (
        <div className="flex items-center justify-end gap-2 pt-1">
          <span className="text-[11px] text-muted-foreground">
            Calculated{" "}
            <span className="font-mono tabular-nums">
              {fmtCurrency(calculated)}
            </span>
          </span>
          {derived?.hasVariance && derived.variance !== undefined && (
            <Badge color="amber" variant="soft" size="sm">
              {fmtSignedCurrency(derived.variance)}
            </Badge>
          )}
          {!isViewer && (
            <button
              type="button"
              onClick={() => commit(calculated)}
              className="inline-flex items-center gap-1 rounded-md border border-primary/30 px-1.5 py-0.5 text-[11px] font-medium text-primary transition-colors hover:border-primary/50 hover:bg-primary/5"
            >
              <Sparkles className="h-3 w-3" />
              Use calculated
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Nested FEG input field
// ---------------------------------------------------------------------------

function DetailField({
  label,
  section,
  fieldKey,
  value,
  displayValue,
  onSave,
  isViewer,
  multiline,
}: {
  label: string;
  section: DetailSection;
  fieldKey: string;
  value?: string | number;
  displayValue?: string;
  onSave: (
    section: DetailSection,
    patch: Record<string, string | number | undefined>,
  ) => void;
  isViewer?: boolean;
  multiline?: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(String(value ?? ""));

  const handleSave = () => {
    const next = multiline
      ? draft.trim() || undefined
      : draft
        ? parseFloat(draft)
        : undefined;
    onSave(section, { [fieldKey]: next });
    setEditing(false);
  };

  if (editing) {
    return (
      <div className="flex items-center justify-between gap-2 py-2">
        <span className="shrink-0 text-sm text-muted-foreground">{label}</span>
        <div className="flex items-center gap-1">
          <Input
            type={multiline ? "text" : "number"}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            className={cn(
              "h-7 text-sm",
              multiline ? "w-56" : "w-28 text-right font-mono",
            )}
            onKeyDown={(e) => e.key === "Enter" && handleSave()}
            autoFocus
          />
          <button
            onClick={handleSave}
            className="rounded p-1 text-emerald-600 hover:bg-emerald-50"
          >
            <Check className="h-3.5 w-3.5" />
          </button>
          <button
            onClick={() => setEditing(false)}
            className="rounded p-1 text-muted-foreground hover:bg-muted"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="group flex items-center justify-between gap-4 py-2">
      <span className="shrink-0 text-sm text-muted-foreground">{label}</span>
      <div className="flex min-w-0 items-center gap-1.5">
        <span
          className={cn(
            "truncate text-right text-sm text-foreground",
            !multiline && "font-mono tabular-nums",
          )}
        >
          {displayValue ?? String(value ?? EM_DASH)}
        </span>
        {!isViewer && (
          <button
            onClick={() => {
              setDraft(String(value ?? ""));
              setEditing(true);
            }}
            className="shrink-0 rounded p-0.5 text-muted-foreground/0 transition-colors hover:bg-muted group-hover:text-muted-foreground"
          >
            <Pencil className="h-3 w-3" />
          </button>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section headers
// ---------------------------------------------------------------------------

function SectionHeader({ children }: { children: React.ReactNode }) {
  return (
    <div className="pb-1.5 pt-6 text-xs font-medium uppercase tracking-widest text-[#121212]">
      {children}
    </div>
  );
}

function CollapsibleSectionHeader({
  children,
  open,
  onToggle,
  meta,
}: {
  children: React.ReactNode;
  open: boolean;
  onToggle: () => void;
  /** Right-aligned summary shown while the section is collapsed. */
  meta?: string;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className="group flex w-full items-center justify-between gap-3 pb-1.5 pt-6 text-xs font-medium uppercase tracking-widest text-[#121212]"
    >
      <span>{children}</span>
      <span className="flex items-center gap-2">
        {meta && !open && (
          <span className="font-mono text-[11px] font-normal normal-case tabular-nums tracking-normal text-muted-foreground">
            {meta}
          </span>
        )}
        <ChevronDown
          className={cn(
            "h-3.5 w-3.5 text-muted-foreground transition-transform",
            open ? "rotate-0" : "-rotate-90",
          )}
        />
      </span>
    </button>
  );
}

/** Inline warning for inputs the calculation needs but doesn't have. */
function MissingInputNotice({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-2.5 py-2 text-[11px] leading-snug text-amber-800 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-200">
      <Info className="mt-px h-3.5 w-3.5 shrink-0" />
      <span>{children}</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Related party
// ---------------------------------------------------------------------------

/**
 * Flags an employee the related parties register recognises.
 *
 * It carries more weight here than on the creditors table: a director or a
 * director's relative is an excluded employee, so nothing they are owed is
 * advanced under FEG. The match is derived rather than stored, so adding
 * someone to the register badges them everywhere at once.
 *
 * Its own component so the query only runs with a job to run it against.
 */
function EmployeeRelatedPartyNote({
  jobId,
  entryId,
  isRelatedParty,
}: {
  jobId: Id<"jobs">;
  entryId: string;
  /** The flag stored on the row, for employees the agent flagged directly. */
  isRelatedParty?: boolean;
}) {
  const { data: relatedFlags } = useQuery(
    convexQuery(
      api.jobs.summarization.agents.relatedParties.publicQueries
        .listRelatedPartyFlagsForJob,
      { jobId },
    ),
  );

  const match = relatedFlags?.employees.find((e) => e.entryId === entryId);

  if (!match) {
    if (!isRelatedParty) return null;
    return (
      <Badge color="amber" variant="soft" size="sm">
        Related party
      </Badge>
    );
  }

  return (
    <div className="flex items-center gap-1.5">
      <RelatedPartyBadge match={match} />
      <span className="text-xs text-muted-foreground">
        {relatedPartyExplanation(match)}
      </span>
    </div>
  );
}

/**
 * Proof of Debt on a preferred-creditor slide-out. Only rendered when this
 * employee has a Form 535 on file — most employees never lodge one.
 */
function EmployeePodSection({
  jobId,
  entryId,
}: {
  jobId: Id<"jobs">;
  entryId: string;
}) {
  const { data: rawPodStatuses } = useQuery(
    convexQuery(
      api.jobs.recoveries.publicQueries.listPodStatusForEmployeeEntries,
      { jobId },
    ),
  );
  const status = useMemo(() => {
    for (const row of rawPodStatuses ?? []) {
      if (row.employeeEntryId === entryId) return row;
    }
    return undefined;
  }, [rawPodStatuses, entryId]);

  if (!status) return null;

  return (
    <>
      <PodStatusSection jobId={jobId} status={status} />
      <div className="mt-2 border-b" />
    </>
  );
}

/**
 * Standing caveat on the headline entitlement figures.
 *
 * The estimates are built to the National Employment Standards minimums, but a
 * modern award or enterprise agreement can lift almost any of them (notice,
 * redundancy, leave loading). This flags that so a practitioner treats the
 * totals as a floor to be checked against the instrument that actually covers
 * the employee, not a final number.
 */
function NesBasisNote() {
  return (
    <div className="mt-3 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-[11px] leading-snug text-amber-800 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-300">
      <AlertTriangle className="mt-px h-3.5 w-3.5 shrink-0" />
      <span>
        These entitlement calculations are based on the National Employment
        Standards. Check them against any specific award or enterprise agreement
        that may apply to this employee.
      </span>
    </div>
  );
}

/**
 * Warns that FEG may not cover this employee.
 *
 * A related party is not automatically an excluded employee — that turns on
 * being a director, or their relative, within the 12 months before appointment
 * — so this asks the practitioner to check rather than zeroing the figure and
 * deciding for them. The estimate keeps calculating either way, because it is
 * still what they are owed; FEG only decides who advances it.
 */
function FegExclusionCallout({ entry }: { entry: EmployeeEntryUI }) {
  const { data: relatedFlags } = useQuery(
    convexQuery(
      api.jobs.summarization.agents.relatedParties.publicQueries
        .listRelatedPartyFlagsForJob,
      { jobId: entry.jobId as Id<"jobs"> },
    ),
  );

  const flagged =
    relatedFlags?.employees.some((e) => e.entryId === entry._id) ||
    entry.isRelatedParty ||
    (entry.relationshipToDirector != null &&
      entry.relationshipToDirector !== "none");

  if (!flagged) return null;

  return (
    <div className="mt-3 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-[11px] leading-snug text-amber-800 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-300">
      <AlertTriangle className="mt-px h-3.5 w-3.5 shrink-0" />
      <span>
        Check whether this is an excluded employee. Directors, and their
        relatives, in the 12 months before appointment cannot claim under FEG —
        the estimate above is still what they are owed, but FEG would not
        advance it.
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

interface EmployeeDetailPanelProps {
  entry: EmployeeEntryUI | null;
  /** Job-level insolvency dates the service calculations measure to. */
  caseDates?: FegCaseDates;
  open: boolean;
  onClose: () => void;
  onDeleted?: () => void;
  isViewer?: boolean;
}

export function EmployeeDetailPanel({
  entry,
  caseDates,
  open,
  onClose,
  onDeleted,
  isViewer,
}: EmployeeDetailPanelProps) {
  const [visible, setVisible] = useState(false);
  const [viewingDocumentId, setViewingDocumentId] =
    useState<Id<"documents"> | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState("");
  const [showSourceDocs, setShowSourceDocs] = useState(true);
  // Edit awaiting practitioner confirmation (all inline edits pass through
  // the shared ConfirmEditDialog before any mutation fires).
  const [pendingEdit, setPendingEdit] = useState<PendingEdit | null>(null);
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({
    wages: true,
    annualLeave: true,
    leaveLoading: false,
    lsl: false,
    piln: false,
    redundancy: false,
    pat: false,
    super: false,
  });

  const toggleSection = (key: string) =>
    setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));

  const updateField = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.summarization.agents.employment.publicMutations
        .updateEmployeeField,
    ),
  });

  const updateEntitlements = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.summarization.agents.employment.publicMutations
        .updateEntitlements,
    ),
  });

  const updateFegDetail = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.summarization.agents.employment.publicMutations.updateFegDetail,
    ),
  });

  const deleteEmployee = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.summarization.agents.employment.publicMutations.deleteEmployee,
    ),
  });

  const toggleExcluded = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.summarization.agents.employment.publicMutations.toggleExcluded,
    ),
  });

  useEffect(() => {
    if (open) {
      requestAnimationFrame(() => setVisible(true));
      setConfirmDelete(false);
    } else {
      setVisible(false);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onClose]);

  const historyUserIds = useMemo<Array<Id<"users">>>(() => {
    if (!entry?.changeHistory) return [];
    const ids = new Set<Id<"users">>();
    for (const change of entry.changeHistory) {
      if (change.userId) ids.add(change.userId);
    }
    return Array.from(ids);
  }, [entry?.changeHistory]);

  const { data: historyUsers } = useQuery({
    ...convexQuery(api.users.queries.listUsersByIds, { ids: historyUserIds }),
    enabled: historyUserIds.length > 0,
  });

  const historyUsersById = useMemo(() => {
    const map = new Map<Id<"users">, ResolvedHistoryUser>();
    for (const u of historyUsers ?? []) map.set(u._id, u);
    return map;
  }, [historyUsers]);

  const derived = useMemo(
    () => (entry ? computeFegDerived(entry, caseDates) : null),
    [entry, caseDates],
  );

  if (!entry || !derived) return null;

  const total = sumEmployeeEntitlements(entry.entitlements);
  const fegTotal = sumFegRecoverable(entry.entitlements);

  // Rows created before the structured name split have no stored parts,
  // so fall back to splitting the display name — the same fallback the
  // FEG export uses, so what is shown here is what gets exported.
  const nameParts = splitEmployeeName(entry.name);
  const familyName = entry.familyName || nameParts.familyName || undefined;
  const firstName = entry.firstName || nameParts.firstName || undefined;

  // Only offer the three bases FEG accepts. A row already carrying one of
  // Morgan's wider values keeps it as a selectable option — flagged — so
  // the picker never renders blank and the practitioner can see why that
  // column exports empty.
  const basisOptions: Array<[string, string]> = [
    ...FEG_EMPLOYMENT_BASIS.map(
      (k) => [k, EMPLOYMENT_BASIS_LABELS[k]] as [string, string],
    ),
    ...(entry.employmentCategory &&
    !isFegEmploymentBasis(entry.employmentCategory)
      ? [
          [
            entry.employmentCategory,
            `${EMPLOYMENT_BASIS_LABELS[entry.employmentCategory]} — not accepted by FEG`,
          ] as [string, string],
        ]
      : []),
  ];

  // All three save paths stage the edit behind the shared confirmation
  // dialog — the mutation only fires once the practitioner confirms
  // (optionally leaving a note).
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleFieldSave = (field: string, value: any) => {
    setPendingEdit({
      fieldLabel: EMPLOYEE_FIELD_LABELS[field] ?? field,
      onConfirm: (note) =>
        updateField.mutateAsync({
          entryId: entry._id as Id<"employeeEntries">,
          field,
          value,
          note,
        }),
    });
  };

  const handleEntitlementsSave = (
    entitlements: Record<string, number | undefined>,
  ) => {
    setPendingEdit({
      fieldLabel: "Entitlements",
      onConfirm: (note) =>
        updateEntitlements.mutateAsync({
          entryId: entry._id as Id<"employeeEntries">,
          entitlements,
          note,
        }),
    });
  };

  // The mutation replaces the whole section, so merge the edited field
  // over whatever is already stored rather than sending it alone.
  const handleDetailSave = (
    section: DetailSection,
    patch: Record<string, string | number | undefined>,
  ) => {
    const existing = (entry[section] ?? {}) as Record<string, unknown>;
    setPendingEdit({
      fieldLabel: EMPLOYEE_FIELD_LABELS[section] ?? section,
      onConfirm: (note) =>
        updateFegDetail.mutateAsync({
          entryId: entry._id as Id<"employeeEntries">,
          section,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any -- pre-existing merge-over-stored pattern for the FEG detail union
          value: { ...existing, ...patch } as any,
          note,
        }),
    });
  };

  const handleDelete = () => {
    deleteEmployee.mutate(
      { entryId: entry._id as Id<"employeeEntries"> },
      {
        onSuccess: () => {
          onDeleted?.();
          onClose();
        },
      },
    );
  };

  const entitlements = (entry.entitlements ?? {}) as Record<
    string,
    number | undefined
  >;
  const wages = entry.wagesDetail ?? {};
  const annual = entry.annualLeaveDetail ?? {};
  const loading = entry.leaveLoadingDetail ?? {};
  const lsl = entry.longServiceLeaveDetail ?? {};
  const piln = entry.pilnDetail ?? {};
  const redundancy = entry.redundancyDetail ?? {};
  const pat = entry.paymentsAfterTermination ?? {};

  const paidTotal = sumPaymentsAfterTermination(pat);
  const netRecoverable = netFegRecoverable(entry.entitlements, pat);

  const missingRate = entry.wageRate == null;
  const missingInsolvencyDate = !derived.insolvencyDate;

  return (
    <>
      {/* Backdrop */}
      <div
        className={cn(
          "fixed inset-0 z-40 transition-opacity duration-200",
          visible
            ? "bg-black/18 dark:bg-black/40"
            : "pointer-events-none bg-transparent",
        )}
        onClick={onClose}
      />

      {/* Panel */}
      <div
        className={cn(
          "fixed right-0 top-0 z-50 h-full w-full max-w-lg overflow-y-auto border-l bg-background shadow-xl transition-transform duration-200 ease-out",
          visible ? "translate-x-0" : "translate-x-full",
        )}
      >
        {/* Header — editable name + role */}
        <div className="sticky top-0 z-10 border-b bg-background/95 px-5 pb-3 pt-5 backdrop-blur-sm">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              {!isViewer && editingName ? (
                <div className="flex items-center gap-1.5">
                  <Input
                    value={nameDraft}
                    onChange={(e) => setNameDraft(e.target.value)}
                    className="h-8 text-lg font-semibold"
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        handleFieldSave("name", nameDraft);
                        setEditingName(false);
                      }
                      if (e.key === "Escape") setEditingName(false);
                    }}
                    autoFocus
                  />
                  <button
                    onClick={() => {
                      handleFieldSave("name", nameDraft);
                      setEditingName(false);
                    }}
                    className="rounded p-1 text-emerald-600 hover:bg-emerald-50"
                  >
                    <Check className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => setEditingName(false)}
                    className="rounded p-1 text-muted-foreground hover:bg-muted"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <div
                  className={cn("group", !isViewer && "cursor-pointer")}
                  onClick={() => {
                    if (!isViewer) {
                      setNameDraft(entry.name);
                      setEditingName(true);
                    }
                  }}
                >
                  <div className="flex items-center gap-1.5">
                    <h2 className="mt-0.5 text-lg font-semibold leading-snug text-foreground">
                      {titleCaseCompanyName(entry.name)}
                    </h2>
                    {!isViewer && (
                      <Pencil className="mt-1 h-3.5 w-3.5 text-muted-foreground/0 transition-colors group-hover:text-muted-foreground" />
                    )}
                  </div>
                </div>
              )}
              {entry.role && (
                <div className="mt-0.5 text-xs text-muted-foreground">
                  {entry.role}
                </div>
              )}
            </div>
            <div className="mt-0.5 flex shrink-0 items-center gap-1">
              {!isViewer && (
                <button
                  onClick={() =>
                    toggleExcluded.mutate({
                      entryId: entry._id as Id<"employeeEntries">,
                      isExcluded: !entry.isExcluded,
                    })
                  }
                  className={cn(
                    "rounded-lg p-1.5 transition-colors hover:bg-muted",
                    entry.isExcluded
                      ? "text-emerald-600 hover:text-emerald-700 dark:text-emerald-400"
                      : "text-muted-foreground hover:text-foreground",
                  )}
                  title={
                    entry.isExcluded
                      ? "Include in totals"
                      : "Exclude from totals"
                  }
                >
                  {entry.isExcluded ? (
                    <Eye className="h-[16px] w-[16px]" />
                  ) : (
                    <EyeOff className="h-[16px] w-[16px]" />
                  )}
                </button>
              )}
              {!isViewer && (
                <button
                  onClick={() => setConfirmDelete(true)}
                  className="rounded-lg p-1.5 text-muted-foreground transition-colors hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-950/30"
                  title="Delete employee"
                >
                  <Trash2 className="h-[16px] w-[16px]" />
                </button>
              )}
              <button
                onClick={onClose}
                className="rounded-lg p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              >
                <X className="h-[18px] w-[18px]" />
              </button>
            </div>
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <EmployeeRelatedPartyNote
              jobId={entry.jobId as Id<"jobs">}
              entryId={entry._id}
              isRelatedParty={entry.isRelatedParty}
            />
            {entry.isManualOverride && (
              <Badge color="emerald" variant="soft" size="sm">
                Manually edited
              </Badge>
            )}
            {entry.isExcluded && (
              <Badge color="gray" variant="soft" size="sm">
                Excluded
              </Badge>
            )}
          </div>
        </div>

        {/* Excluded notice */}
        {entry.isExcluded && (
          <div className="mx-5 mt-4 flex items-start gap-2 rounded-lg border border-border bg-muted/50 px-3 py-2">
            <EyeOff className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            <p className="text-xs leading-relaxed text-muted-foreground">
              This employee is excluded — they stay in the register but are left
              out of the preferred-creditor totals, the Proof of Debt process
              and reporting.
            </p>
          </div>
        )}

        {/* Delete confirmation */}
        {confirmDelete && (
          <div className="mx-5 mt-4 rounded-lg border border-red-200 bg-red-50 p-3 dark:border-red-800 dark:bg-red-950/30">
            <p className="text-sm text-red-700 dark:text-red-300">
              Remove this employee from the register?
            </p>
            <div className="mt-2 flex gap-2">
              <Button
                size="sm"
                variant="destructive"
                onClick={handleDelete}
                disabled={deleteEmployee.isPending}
              >
                {deleteEmployee.isPending ? "Removing..." : "Remove"}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setConfirmDelete(false)}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}

        <div className="px-5 pb-8">
          {/* Totals banner */}
          {total > 0 && (
            <div className="mt-4 grid grid-cols-2 gap-3">
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 dark:border-emerald-800 dark:bg-emerald-950/30">
                <div className="mb-1 text-[10px] font-semibold uppercase tracking-widest text-emerald-600 dark:text-emerald-400">
                  Est FEG entitlements
                </div>
                <div className="font-mono text-xl font-bold tabular-nums text-emerald-700 dark:text-emerald-300">
                  {fmtCurrency(fegTotal)}
                </div>
              </div>
              <div className="rounded-lg border bg-muted/50 p-4">
                <div className="mb-1 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
                  Total entitlements
                </div>
                <div className="font-mono text-xl font-bold tabular-nums text-foreground">
                  {fmtCurrency(total)}
                </div>
                <div className="mt-1 text-[10px] text-muted-foreground">
                  {
                    ENTITLEMENT_SOURCE_HINT[
                      entry.entitlementsSource ?? "document"
                    ]
                  }
                </div>
              </div>
            </div>
          )}

          {total > 0 && <NesBasisNote />}

          <FegExclusionCallout entry={entry} />

          {/* Where the figures above came from */}
          <EntitlementEstimatesSection
            entry={entry}
            recordedTotal={total}
            isViewer={isViewer}
          />

          {/* Personal details */}
          <SectionHeader>Personal details</SectionHeader>
          <EditableField
            label="Family name"
            value={familyName}
            field="familyName"
            onSave={handleFieldSave}
            isViewer={isViewer}
          />
          <EditableField
            label="First name"
            value={firstName}
            field="firstName"
            onSave={handleFieldSave}
            isViewer={isViewer}
          />
          <EditableField
            label="Date of birth"
            value={entry.dateOfBirth}
            displayValue={fmtDateLong(entry.dateOfBirth)}
            field="dateOfBirth"
            type="date"
            onSave={handleFieldSave}
            isViewer={isViewer}
          />
          <EnumField
            label="Residency status"
            value={entry.residencyStatus}
            options={Object.entries(RESIDENCY_LABELS)}
            onChange={(v) => handleFieldSave("residencyStatus", v)}
            isViewer={isViewer}
          />
          <EnumField
            label="Relationship to director"
            value={entry.relationshipToDirector}
            options={Object.entries(DIRECTOR_RELATIONSHIP_LABELS)}
            onChange={(v) => handleFieldSave("relationshipToDirector", v)}
            isViewer={isViewer}
          />
          <div className="mt-2 border-b" />

          {/* Employment */}
          <SectionHeader>Employment</SectionHeader>
          <EditableField
            label="Role"
            value={entry.role}
            field="role"
            onSave={handleFieldSave}
            isViewer={isViewer}
          />
          <EnumField
            label="Employment type"
            value={entry.employmentType}
            options={Object.entries(EMPLOYMENT_TYPE_LABELS)}
            onChange={(v) => handleFieldSave("employmentType", v)}
            isViewer={isViewer}
          />
          <EnumField
            label="Basis of employment"
            value={entry.employmentCategory}
            options={basisOptions}
            onChange={(v) => handleFieldSave("employmentCategory", v)}
            isViewer={isViewer}
          />
          <EnumField
            label="State of employment"
            value={entry.stateOfEmployment}
            options={EMPLOYMENT_STATES.map((s) => [s, s] as [string, string])}
            onChange={(v) => handleFieldSave("stateOfEmployment", v)}
            isViewer={isViewer}
          />
          <EditableField
            label="Start date"
            value={entry.startDate}
            displayValue={fmtDateLong(entry.startDate)}
            field="startDate"
            type="date"
            onSave={handleFieldSave}
            isViewer={isViewer}
          />
          <EditableField
            label="Avg weekly hours"
            value={entry.averageWeeklyHours}
            displayValue={fmtHours(entry.averageWeeklyHours)}
            field="averageWeeklyHours"
            type="number"
            onSave={handleFieldSave}
            isViewer={isViewer}
          />
          <EditableField
            label="Hourly rate"
            value={entry.wageRate}
            displayValue={
              entry.wageRate != null
                ? `$${entry.wageRate.toFixed(2)}/hr`
                : undefined
            }
            field="wageRate"
            type="number"
            onSave={handleFieldSave}
            isViewer={isViewer}
          />
          <EditableField
            label="Regular weekly allowances"
            value={entry.regularWeeklyAllowances}
            displayValue={fmtCurrency(entry.regularWeeklyAllowances)}
            field="regularWeeklyAllowances"
            type="number"
            onSave={handleFieldSave}
            isViewer={isViewer}
          />
          <DerivedRow
            label="Base weekly wage"
            hint="Hourly rate x average weekly hours"
            value={fmtCurrency(derived.baseWeeklyWage)}
          />
          <DerivedRow
            label="Total weekly wage"
            hint="Base weekly wage plus regular allowances"
            value={fmtCurrency(derived.totalWeeklyWage)}
          />
          <EditableField
            label="Award classification"
            value={entry.awardClassification}
            field="awardClassification"
            onSave={handleFieldSave}
            isViewer={isViewer}
          />
          {missingRate && (
            <MissingInputNotice>
              Add an hourly rate and average weekly hours to calculate the
              weekly wages and every leave entitlement below.
            </MissingInputNotice>
          )}
          <div className="mt-2 border-b" />

          {/* End of employment */}
          <SectionHeader>End of employment</SectionHeader>
          <EditableField
            label="Termination date"
            value={entry.terminationDate}
            displayValue={fmtDateLong(entry.terminationDate)}
            field="terminationDate"
            type="date"
            onSave={handleFieldSave}
            isViewer={isViewer}
          />
          <EnumField
            label="Termination type"
            value={entry.terminationType}
            options={Object.entries(TERMINATION_TYPE_LABELS)}
            onChange={(v) => handleFieldSave("terminationType", v)}
            isViewer={isViewer}
          />
          <EditableField
            label="Notification date"
            value={entry.notificationDate}
            displayValue={fmtDateLong(entry.notificationDate)}
            field="notificationDate"
            type="date"
            onSave={handleFieldSave}
            isViewer={isViewer}
          />
          <DerivedRow
            label="Service at IP appointment"
            hint={
              derived.insolvencyDate
                ? `Start date to ${fmtDateLong(derived.insolvencyDate)}`
                : undefined
            }
            value={fmtYears(derived.serviceAtAppointment)}
          />
          <DerivedRow
            label="Service at termination"
            value={fmtYears(derived.serviceAtTermination)}
          />
          {missingInsolvencyDate && (
            <MissingInputNotice>
              Service at appointment needs the administration or liquidation
              date. Add them under Job Settings &rarr; Appointment &rarr;
              Insolvency Dates.
            </MissingInputNotice>
          )}
          <div className="mt-2 border-b" />

          {/* Unpaid wages */}
          <CollapsibleSectionHeader
            open={openSections.wages}
            onToggle={() => toggleSection("wages")}
            meta={fmtCurrency(entitlements.unpaidWages)}
          >
            Unpaid wages
          </CollapsibleSectionHeader>
          {openSections.wages && (
            <>
              <AmountRow
                label="Unpaid wages claimed"
                entKey="unpaidWages"
                allEntitlements={entitlements}
                onSave={handleEntitlementsSave}
                isViewer={isViewer}
              />
              <DetailField
                label="Total outstanding"
                section="wagesDetail"
                fieldKey="totalOutstanding"
                value={wages.totalOutstanding}
                displayValue={fmtCurrency(wages.totalOutstanding)}
                onSave={handleDetailSave}
                isViewer={isViewer}
              />
              <DetailField
                label="Unpaid in period"
                section="wagesDetail"
                fieldKey="unpaidInPeriod"
                value={wages.unpaidInPeriod}
                displayValue={fmtCurrency(wages.unpaidInPeriod)}
                onSave={handleDetailSave}
                isViewer={isViewer}
              />
              <DetailField
                label="Allowances in period"
                section="wagesDetail"
                fieldKey="allowancesInPeriod"
                value={wages.allowancesInPeriod}
                displayValue={fmtCurrency(wages.allowancesInPeriod)}
                onSave={handleDetailSave}
                isViewer={isViewer}
              />
              <DetailField
                label="Commission in period"
                section="wagesDetail"
                fieldKey="commissionInPeriod"
                value={wages.commissionInPeriod}
                displayValue={fmtCurrency(wages.commissionInPeriod)}
                onSave={handleDetailSave}
                isViewer={isViewer}
              />
              <DetailField
                label="Other entitlements"
                section="wagesDetail"
                fieldKey="otherEntitlements"
                value={wages.otherEntitlements}
                displayValue={fmtCurrency(wages.otherEntitlements)}
                onSave={handleDetailSave}
                isViewer={isViewer}
              />
              <DetailField
                label="Comments"
                section="wagesDetail"
                fieldKey="comments"
                value={wages.comments}
                onSave={handleDetailSave}
                isViewer={isViewer}
                multiline
              />
            </>
          )}
          <div className="mt-2 border-b" />

          {/* Annual leave */}
          <CollapsibleSectionHeader
            open={openSections.annualLeave}
            onToggle={() => toggleSection("annualLeave")}
            meta={fmtCurrency(entitlements.accruedAnnualLeave)}
          >
            Annual leave
          </CollapsibleSectionHeader>
          {openSections.annualLeave && (
            <>
              <DetailField
                label="Hours at termination"
                section="annualLeaveDetail"
                fieldKey="hoursAtTermination"
                value={annual.hoursAtTermination}
                displayValue={fmtHours(annual.hoursAtTermination)}
                onSave={handleDetailSave}
                isViewer={isViewer}
              />
              <AmountRow
                label="Annual leave owing"
                entKey="accruedAnnualLeave"
                allEntitlements={entitlements}
                derived={derived.annualLeave}
                onSave={handleEntitlementsSave}
                isViewer={isViewer}
              />
              <DetailField
                label="Winding-up cost"
                section="annualLeaveDetail"
                fieldKey="windUpCost"
                value={annual.windUpCost}
                displayValue={fmtCurrency(annual.windUpCost)}
                onSave={handleDetailSave}
                isViewer={isViewer}
              />
              <DetailField
                label="Comments"
                section="annualLeaveDetail"
                fieldKey="comments"
                value={annual.comments}
                onSave={handleDetailSave}
                isViewer={isViewer}
                multiline
              />
            </>
          )}
          <div className="mt-2 border-b" />

          {/* Leave loading */}
          <CollapsibleSectionHeader
            open={openSections.leaveLoading}
            onToggle={() => toggleSection("leaveLoading")}
            meta={fmtCurrency(entitlements.leaveLoading)}
          >
            Leave loading
          </CollapsibleSectionHeader>
          {openSections.leaveLoading && (
            <>
              <DetailField
                label="Loading rate"
                section="leaveLoadingDetail"
                fieldKey="ratePercent"
                value={loading.ratePercent}
                displayValue={
                  loading.ratePercent != null
                    ? `${loading.ratePercent}%`
                    : EM_DASH
                }
                onSave={handleDetailSave}
                isViewer={isViewer}
              />
              <AmountRow
                label="Leave loading owing"
                entKey="leaveLoading"
                allEntitlements={entitlements}
                derived={derived.leaveLoading}
                onSave={handleEntitlementsSave}
                isViewer={isViewer}
              />
              <DetailField
                label="Winding-up cost"
                section="leaveLoadingDetail"
                fieldKey="windUpCost"
                value={loading.windUpCost}
                displayValue={fmtCurrency(loading.windUpCost)}
                onSave={handleDetailSave}
                isViewer={isViewer}
              />
              <DetailField
                label="Comments"
                section="leaveLoadingDetail"
                fieldKey="comments"
                value={loading.comments}
                onSave={handleDetailSave}
                isViewer={isViewer}
                multiline
              />
              {annual.hoursAtTermination == null && (
                <MissingInputNotice>
                  Leave loading is calculated from the annual leave hours at
                  termination — add those in the Annual leave section.
                </MissingInputNotice>
              )}
            </>
          )}
          <div className="mt-2 border-b" />

          {/* Long service leave */}
          <CollapsibleSectionHeader
            open={openSections.lsl}
            onToggle={() => toggleSection("lsl")}
            meta={fmtCurrency(entitlements.accruedLongServiceLeave)}
          >
            Long service leave
          </CollapsibleSectionHeader>
          {openSections.lsl && (
            <>
              <DetailField
                label="Hours at termination"
                section="longServiceLeaveDetail"
                fieldKey="hoursAtTermination"
                value={lsl.hoursAtTermination}
                displayValue={fmtHours(lsl.hoursAtTermination)}
                onSave={handleDetailSave}
                isViewer={isViewer}
              />
              <AmountRow
                label="Long service leave owing"
                entKey="accruedLongServiceLeave"
                allEntitlements={entitlements}
                derived={derived.longServiceLeave}
                onSave={handleEntitlementsSave}
                isViewer={isViewer}
              />
              <DetailField
                label="Winding-up cost"
                section="longServiceLeaveDetail"
                fieldKey="windUpCost"
                value={lsl.windUpCost}
                displayValue={fmtCurrency(lsl.windUpCost)}
                onSave={handleDetailSave}
                isViewer={isViewer}
              />
              <DetailField
                label="Comments"
                section="longServiceLeaveDetail"
                fieldKey="comments"
                value={lsl.comments}
                onSave={handleDetailSave}
                isViewer={isViewer}
                multiline
              />
            </>
          )}
          <div className="mt-2 border-b" />

          {/* Payment in lieu of notice */}
          <CollapsibleSectionHeader
            open={openSections.piln}
            onToggle={() => toggleSection("piln")}
            meta={fmtCurrency(entitlements.paymentInLieuOfNotice)}
          >
            Payment in lieu of notice
          </CollapsibleSectionHeader>
          {openSections.piln && (
            <>
              <DetailField
                label="Weeks owing"
                section="pilnDetail"
                fieldKey="weeksOwing"
                value={piln.weeksOwing}
                displayValue={fmtWeeks(piln.weeksOwing)}
                onSave={handleDetailSave}
                isViewer={isViewer}
              />
              <AmountRow
                label="Notice owing"
                entKey="paymentInLieuOfNotice"
                allEntitlements={entitlements}
                derived={derived.piln}
                onSave={handleEntitlementsSave}
                isViewer={isViewer}
              />
              <DerivedRow
                label="Calculated on"
                hint="Notice includes regular allowances"
                value={`${fmtCurrency(derived.totalWeeklyWage)}/wk`}
              />
              <DetailField
                label="Winding-up cost"
                section="pilnDetail"
                fieldKey="windUpCost"
                value={piln.windUpCost}
                displayValue={fmtCurrency(piln.windUpCost)}
                onSave={handleDetailSave}
                isViewer={isViewer}
              />
              <DetailField
                label="Comments"
                section="pilnDetail"
                fieldKey="comments"
                value={piln.comments}
                onSave={handleDetailSave}
                isViewer={isViewer}
                multiline
              />
            </>
          )}
          <div className="mt-2 border-b" />

          {/* Redundancy */}
          <CollapsibleSectionHeader
            open={openSections.redundancy}
            onToggle={() => toggleSection("redundancy")}
            meta={fmtCurrency(entitlements.redundancyPay)}
          >
            Redundancy
          </CollapsibleSectionHeader>
          {openSections.redundancy && (
            <>
              <DetailField
                label="Weeks owing"
                section="redundancyDetail"
                fieldKey="weeksOwing"
                value={redundancy.weeksOwing}
                displayValue={fmtWeeks(redundancy.weeksOwing)}
                onSave={handleDetailSave}
                isViewer={isViewer}
              />
              <AmountRow
                label="Redundancy owing"
                entKey="redundancyPay"
                allEntitlements={entitlements}
                derived={derived.redundancy}
                onSave={handleEntitlementsSave}
                isViewer={isViewer}
              />
              <DerivedRow
                label="Calculated on"
                hint="Redundancy excludes allowances"
                value={`${fmtCurrency(derived.baseWeeklyWage)}/wk`}
              />
              <DetailField
                label="Winding-up cost"
                section="redundancyDetail"
                fieldKey="windUpCost"
                value={redundancy.windUpCost}
                displayValue={fmtCurrency(redundancy.windUpCost)}
                onSave={handleDetailSave}
                isViewer={isViewer}
              />
              <DetailField
                label="Comments"
                section="redundancyDetail"
                fieldKey="comments"
                value={redundancy.comments}
                onSave={handleDetailSave}
                isViewer={isViewer}
                multiline
              />
            </>
          )}
          <div className="mt-2 border-b" />

          {/* Payments after termination */}
          <CollapsibleSectionHeader
            open={openSections.pat}
            onToggle={() => toggleSection("pat")}
            meta={paidTotal > 0 ? fmtCurrency(paidTotal) : undefined}
          >
            Payments after termination
          </CollapsibleSectionHeader>
          {openSections.pat && (
            <>
              <p className="pb-1 text-[11px] leading-snug text-muted-foreground">
                Amounts already paid to the employee, or to someone on their
                behalf — a redundancy fund balance, a partial dividend. FEG
                deducts these from what it advances, so record them here rather
                than netting them off the entitlements above.
              </p>
              <DetailField
                label="Unpaid wages paid"
                section="paymentsAfterTermination"
                fieldKey="unpaidWages"
                value={pat.unpaidWages}
                displayValue={fmtCurrency(pat.unpaidWages)}
                onSave={handleDetailSave}
                isViewer={isViewer}
              />
              <DetailField
                label="Annual leave paid"
                section="paymentsAfterTermination"
                fieldKey="annualLeave"
                value={pat.annualLeave}
                displayValue={fmtCurrency(pat.annualLeave)}
                onSave={handleDetailSave}
                isViewer={isViewer}
              />
              <DetailField
                label="Long service leave paid"
                section="paymentsAfterTermination"
                fieldKey="longServiceLeave"
                value={pat.longServiceLeave}
                displayValue={fmtCurrency(pat.longServiceLeave)}
                onSave={handleDetailSave}
                isViewer={isViewer}
              />
              <DetailField
                label="Notice paid"
                section="paymentsAfterTermination"
                fieldKey="piln"
                value={pat.piln}
                displayValue={fmtCurrency(pat.piln)}
                onSave={handleDetailSave}
                isViewer={isViewer}
              />
              <DetailField
                label="Redundancy paid"
                section="paymentsAfterTermination"
                fieldKey="redundancy"
                value={pat.redundancy}
                displayValue={fmtCurrency(pat.redundancy)}
                onSave={handleDetailSave}
                isViewer={isViewer}
              />
              {paidTotal > 0 && (
                <DerivedRow
                  label="Est FEG Entitlement after payments"
                  hint="Excludes superannuation and winding-up costs"
                  value={fmtCurrency(netRecoverable)}
                />
              )}
              <DetailField
                label="Comments"
                section="paymentsAfterTermination"
                fieldKey="comments"
                value={pat.comments}
                onSave={handleDetailSave}
                isViewer={isViewer}
                multiline
              />
            </>
          )}
          <div className="mt-2 border-b" />

          {/* Superannuation */}
          <CollapsibleSectionHeader
            open={openSections.super}
            onToggle={() => toggleSection("super")}
            meta={fmtCurrency(entitlements.unpaidSuper)}
          >
            Superannuation
          </CollapsibleSectionHeader>
          {openSections.super && (
            <>
              <AmountRow
                label="Unpaid superannuation"
                entKey="unpaidSuper"
                allEntitlements={entitlements}
                onSave={handleEntitlementsSave}
                isViewer={isViewer}
              />
              <p className="pb-1 text-[11px] leading-snug text-muted-foreground">
                Not recoverable under FEG — unpaid superannuation is claimed
                through the superannuation guarantee charge via the ATO. It is
                still counted in the total entitlements above.
              </p>
            </>
          )}
          <div className="mt-2 border-b" />

          <EmployeePodSection
            jobId={entry.jobId as Id<"jobs">}
            entryId={entry._id}
          />

          {/* Notes */}
          <NotesSection
            value={entry.notes ?? ""}
            onSave={(v) => handleFieldSave("notes", v)}
            isViewer={isViewer}
          />
          <div className="mt-4 border-b" />

          {/* Source documents */}
          {entry.sourceDocuments && entry.sourceDocuments.length > 0 && (
            <>
              <CollapsibleSectionHeader
                open={showSourceDocs}
                onToggle={() => setShowSourceDocs((v) => !v)}
              >
                Source documents
              </CollapsibleSectionHeader>
              {showSourceDocs && (
                <div className="flex flex-col gap-2 pt-2.5">
                  {entry.sourceDocuments.map((doc, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() =>
                        setViewingDocumentId(doc.documentId as Id<"documents">)
                      }
                      className="group flex w-full items-center gap-3 rounded-lg border px-3 py-2.5 text-left transition-colors hover:bg-muted/50"
                    >
                      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-muted">
                        <FileText className="h-3.5 w-3.5 text-muted-foreground" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-medium text-foreground">
                          {doc.fileName}
                        </div>
                      </div>
                      <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground/30 transition-colors group-hover:text-muted-foreground" />
                    </button>
                  ))}
                </div>
              )}
              <div className="mt-4 border-b" />
            </>
          )}

          {/* History */}
          {entry.changeHistory && (
            <div className="mt-4">
              <ChangeHistory
                history={entry.changeHistory}
                usersById={historyUsersById}
                formatters={EMPLOYEE_FIELD_FORMATTERS}
                fieldLabels={EMPLOYEE_FIELD_LABELS}
              />
            </div>
          )}
        </div>
      </div>

      <DocumentDetail
        documentId={viewingDocumentId}
        isViewer
        open={viewingDocumentId !== null}
        onClose={() => setViewingDocumentId(null)}
      />

      {/* Confirmation gate for every inline edit in this panel */}
      <ConfirmEditDialog
        edit={pendingEdit}
        onClose={() => setPendingEdit(null)}
      />
    </>
  );
}
